import React from 'react';
import { useRouter } from 'next/router';
import ErrorPage from 'next/error';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import htmlToText from 'html-to-text';
import {
  getPost,
  getMorePosts,
  getCategoriess,
  getAllPostsWithSlug,
} from '../../utils/api/wordpress-api';
import Single from '../../components/Articles/Single';
import ArticlesSubHeader from '../../components/Articles/SubHeader';
import StartNow from '../../components/organisms/StartNow';
import BlogSEO from '../../components/SEO/ForBlog';
import { checkLocaleAllowed } from '../blog';
import LoadShareloWidget from '../../components/ShareloWidget';

interface Props {
  post: any;
  posts: any;
  categories: any[];
}

function Post({ post, posts, categories }: Props) {
  const { t } = useTranslation('blog');
  const router = useRouter();
  const { locale } = router;

  if (!router.isFallback && !post) {
    return <ErrorPage statusCode={404} />;
  }
  if (!checkLocaleAllowed(locale)) {
    return <ErrorPage statusCode={404} />;
  }

  return (
    <>
      {router.isFallback ? (
        <h4>Loading…</h4>
      ) : (
        <>
          <article>
            <BlogSEO
              title={t('title.post', { title: post.title })}
              imageUrl={post.featuredImage?.node?.sourceUrl}
              desc={htmlToText.fromString(post.excerpt)}
            />
            <ArticlesSubHeader categories={categories} />
            <Single
              content={post.content}
              tags={post.tags.edges}
              slug={post.slug}
              title={post.title}
              avatarUrl={post.author.node.avatar.url}
              authorName={post.author.node.name}
              authorSlug={post.author.node.slug}
              authorDescription={post.author.node.description}
              date={post.date}
              thumbnail={post.featuredImage?.node?.sourceUrl}
              category={post.categories.edges[0]?.node}
              morePosts={posts?.edges}
            />
          </article>
          <StartNow />
        </>
      )}
      <LoadShareloWidget storyId="3SwyMK557vNxC6neTyVBL" />
    </>
  );
}

const fetchPathsPromise = async (locale: string) => {
  try {
    // Only pre-generate the 20 most recent posts per locale at build time.
    // Older posts are generated on-demand via fallback: 'blocking'.
    const allPosts = await getAllPostsWithSlug(locale, 20);
    if (!allPosts?.edges) return [];
    return allPosts.edges.map((edge: any) => ({
      params: {
        slug: edge.node.slug,
      },
      locale,
    }));
  } catch (error) {
    console.warn(`Failed to fetch blog paths for locale ${locale}:`, error);
    return [];
  }
};

export const getStaticPaths = async () => {
  const locales = ['ja', 'en', 'es', 'pt'];
  const result = await Promise.all(
    locales.map((locale: string) => fetchPathsPromise(locale)),
  );
  const paths = result.flat();
  return {
    paths,
    fallback: 'blocking',
  };
};

export async function getStaticProps({ preview = false, params, locale }) {
  try {
    const post = await getPost(params.slug, locale);
    if (!post) {
      return { notFound: true, revalidate: 60 };
    }
    const category = post?.categories?.edges[0]?.node?.slug;
    const posts = await getMorePosts(params.slug, category, locale);
    const categoriesData = await getCategoriess(locale);
    return {
      props: {
        ...(await serverSideTranslations(locale, ['common', 'blog'])),
        post,
        posts,
        preview,
        categories: categoriesData?.categories || [],
        locale,
      },
      revalidate: 3600, // Regenerate page every hour
    };
  } catch (error) {
    console.error(`Failed to fetch blog post ${params.slug}:`, error);
    return { notFound: true, revalidate: 60 };
  }
}

export default Post;
